import { canAccessFinalCostVerificationDashboard, canQueryDashboardOrderSlice, hasPermission, PERM } from '../accessPermissions';

/** 待办卡片 Tab 切片：有任一即显示处理中订单区块（不再单独控卡片入口） */
export const PENDING_ORDER_TAB_PERMS = Object.freeze([
	PERM.DASHBOARD_ORDER_APPROVING,
	PERM.DASHBOARD_ORDER_PREPAYMENT,
	PERM.DASHBOARD_ORDER_CONTRACT,
	PERM.DASHBOARD_ORDER_DELIVERING,
	PERM.DASHBOARD_ORDER_ACCEPTING,
	PERM.DASHBOARD_ORDER_AR,
	PERM.DASHBOARD_ORDER_APPLING,
	PERM.DASHBOARD_ORDER_REJECTED,
	PERM.DASHBOARD_ORDER_PROCESSING_BUYER,
	PERM.DASHBOARD_ORDER_PROCESSING_FINANCE,
	PERM.DASHBOARD_ORDER_FINAL_COST_VERIFICATION,
]);

/** 处理中订单卡片：任一待办 Tab 权限即具备 */
export const canAccessPendingOrderCard = (userInfo) => {
	if (!userInfo) return false;
	return PENDING_ORDER_TAB_PERMS.some((key) => canQueryDashboardOrderSlice(userInfo, key));
};

/** 是否展示「进程(采购)」Tab */
export const shouldShowBuyerProcessingTab = (userInfo) => canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_PROCESSING_BUYER);

/** 是否展示「进程(财务)」Tab */
export const shouldShowFinanceProcessingTab = (userInfo) => canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_PROCESSING_FINANCE);

/** 是否订阅进程列表接口（采购、财务有任一即拉） */
export const shouldFetchProcessingPendingAggregate = (userInfo) => shouldShowBuyerProcessingTab(userInfo) || shouldShowFinanceProcessingTab(userInfo);

export const shouldShowDeliveringPendingTab = (userInfo) => canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_DELIVERING);

export const shouldShowContractPendingTab = (userInfo) => canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_CONTRACT);

/**
 * 应收账款栏目 id。
 * 全部订单用全库键；销售、助理、财务用 Finance 键。
 * 两套键请求的是同一个接口，行范围由后端归属决定。
 */
export const resolveAccountsReceivablePendingTabKey = (userInfo) => {
	const wide = hasPermission(userInfo, PERM.ORDER_QUERY_TENANT_ALL);
	const scopedOwner = ['sale', 'assistant', 'finance'].includes(userInfo?.role);
	if (!wide && scopedOwner) return 'accountsReceivablePendingFinance';
	return 'accountsReceivablePending';
};

export const buildVisibleDashboardOrderPendingTabs = ({ userInfo, orderPerformanceFeaturesEnabled, counts }) => {
	if (!userInfo) return [];
	if (!canAccessPendingOrderCard(userInfo)) return [];

	const tabs = [];

	if (canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_APPROVING)) {
		tabs.push({
			key: 'approvalPending',
			subTitle: '待审批',
			title: 'Approving',
			icon: 'feather',
			count: counts.myApprovals,
		});
	}

	if (canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_APPLING)) {
		tabs.push({
			key: 'Applying',
			subTitle: '申请中',
			title: 'Applying',
			icon: 'fa-paper-plane',
			count: counts.applyingOrders,
		});
	}

	if (canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_REJECTED)) {
		tabs.push({
			key: 'Rejected',
			subTitle: '被驳回',
			title: 'Rejected',
			icon: 'fa-circle-xmark',
			count: counts.rejectedOrders ?? 0,
		});
	}

	if (canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_PREPAYMENT)) {
		tabs.push({
			key: 'prePaymentOrder',
			subTitle: '预付款',
			title: 'PrePay',
			icon: 'fa-sack-dollar',
			count: counts.prePaymentOrders,
		});
	}

	if (shouldShowContractPendingTab(userInfo)) {
		tabs.push({
			key: 'ContractPending',
			subTitle: '待签约',
			title: 'Contract',
			icon: 'fa-file-contract',
			count: counts.contractOrders,
		});
	}

	if (shouldShowDeliveringPendingTab(userInfo)) {
		tabs.push({
			key: 'deliveryPending',
			subTitle: '待发货',
			title: 'Delivery',
			icon: 'fa-truck-plane',
			count: counts.deliveringOrders,
		});
	}

	if (canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_ACCEPTING)) {
		tabs.push({
			key: 'acceptPending',
			subTitle: '待验收',
			title: 'Accepting',
			icon: 'fa-truck-ramp-box',
			count: counts.acceptingOrders,
		});
	}

	if (canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_AR)) {
		tabs.push({
			key: resolveAccountsReceivablePendingTabKey(userInfo),
			subTitle: '应收账款',
			title: 'AccountsReceivable',
			icon: 'fa-fire-flame-curved',
			count: counts.arOrders,
		});
	}

	if (shouldShowBuyerProcessingTab(userInfo)) {
		tabs.push({
			key: 'ProcessingOrderBuyer',
			subTitle: '进程(采购)',
			title: 'ProcessingBuyer',
			icon: 'fa-mountain-sun',
			count: counts.buyerProcessingOrders,
		});
	}

	if (shouldShowFinanceProcessingTab(userInfo)) {
		tabs.push({
			key: 'ProcessingOrderFinance',
			subTitle: '进程(财务)',
			title: 'ProcessingFinance',
			icon: 'fa-mountain-sun',
			count: counts.financeProcessingOrders,
		});
	}

	if (orderPerformanceFeaturesEnabled && canAccessFinalCostVerificationDashboard(userInfo)) {
		tabs.push({
			key: 'finalCostVerification',
			subTitle: '待核验',
			title: 'Verify',
			icon: 'fa-clipboard-check',
			count: counts.finalCostVerificationOrders,
		});
	}

	return tabs;
};
